// cSpell:ignore forfait Forfait privatieve
// A lease's monthly provision, as the settlement weighs it.
//
// Provisions live on the rental contract (no separate store): the common and
// non-common cost fields plus every additional cost line, each counted when
// its price type is PROVISION (FIXED costs are not regularized). Always the
// `current*` amounts — the amount in effect right now; provisionHistory.ts
// reconstructs earlier months from the history.

import { EContractCostsPriceType } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { roundCents } from "./money.utils";
import { IIndexedProvisionField, ProvisionSlice } from "./provisionHistory";

const PERCENT = 100;

// Two amounts within half a cent are the same amount.
const AMOUNT_TOLERANCE = 0.005;

interface IContractCostLine {
  priceType?: string | null;
  description?: string | null;
  currentPrice?: number | null;
  startPrice?: number | null;
  priceVat?: number | null;
  priceNoVatType?: string | null;
}

// The lease fields this needs, as the contract API returns them.
export interface IContractProvisionSource {
  priceTypeCommonCosts?: string | null;
  priceTypeNonCommonCosts?: string | null;
  currentCostsCommon?: number | null;
  currentCostsNonCommon?: number | null;
  startCostsCommon?: number | null;
  startCostsNonCommon?: number | null;
  costsCommonVat?: number | null;
  costsNonCommonVat?: number | null;
  costsCommonNoVatType?: string | null;
  costsNonCommonNoVatType?: string | null;
  additionalCosts?: IContractCostLine[] | null;
}

// One provision line of the lease, as the rental contract names it: the
// common costs, the non-common ("privatieve") costs, or one of its extra cost
// lines. Only PROVISION lines; a forfait line is no provision.
export type TProvisionLineKind = "common" | "nonCommon" | "additional";

// The keys of the lease's provision lines (IProvisionLine.key).
export const COMMON_LINE_KEY = "common";
export const NON_COMMON_LINE_KEY = "nonCommon";
export const ADDITIONAL_LINE_KEY_PREFIX = "additional:";

export interface IProvisionLine {
  // "common", "nonCommon", or "additional:<index>" with the line's index in
  // the contract's additional costs.
  key: string;
  kind: TProvisionLineKind;
  // The slice the engine settles it in: the common costs in "common", the
  // non-common costs and every extra line in "private".
  slice: ProvisionSlice;
  // An extra line's own description on the contract.
  description?: string;
  // What the tenant pays a month for it today, incl. the VAT the lease
  // charges on it, rounded per line as monthlyProvision* are.
  monthly: number;
}

export interface IContractProvision {
  // What the tenant pays a month, incl. the VAT the lease charges on each
  // line: in total, and per slice.
  monthlyProvision: number;
  monthlyProvisionCommon: number;
  monthlyProvisionPrivate: number;
  // Per slice: the provision still equals the lease's start amount.
  provisionUnchanged: Record<ProvisionSlice, boolean>;
  // Per slice: the lease field its indexations track (absent when that field
  // is no provision).
  provisionIndexed: Partial<Record<ProvisionSlice, IIndexedProvisionField>>;
  // Per slice: the lease charges VAT on (part of) that provision.
  provisionVatIncluded: Record<ProvisionSlice, boolean>;
  // Per slice: the factor that adds the VAT the lease charges on every
  // provision line of that slice (1 = none), null when its lines carry
  // different rates; absent when the slice has no provision line.
  provisionVatFactor: Partial<Record<ProvisionSlice, number | null>>;
  // The lease's provision lines in contract order (common, non-common, the
  // extra lines). Per slice their monthly amounts add up to that slice's.
  provisionLines: IProvisionLine[];
}

const isProvision = (priceType?: string | null): boolean =>
  priceType === EContractCostsPriceType.Provision;

// Factor that adds the VAT a lease charges on one of its lines; a "no VAT"
// reason means none.
const vatFactorOf = (params: {
  vatPct?: number | null;
  noVatType?: string | null;
}): number =>
  params.noVatType ? 1 : 1 + (Number(params.vatPct) || 0) / PERCENT;

const sameAmount = (a?: number | null, b?: number | null): boolean =>
  Math.abs(Number(a ?? 0) - Number(b ?? 0)) < AMOUNT_TOLERANCE;

// The one VAT factor of a slice's provision lines (at least one), null when
// they differ (see provisionVatFactor).
const sharedVatFactor = (factors: number[]): number | null =>
  factors.every(factor => factor === factors[0]) ? factors[0] : null;

const indexedField = (params: {
  current?: number | null;
  start?: number | null;
  vatFactor: number;
  otherLinesChanged?: boolean;
}): IIndexedProvisionField => ({
  current: Number(params.current ?? 0),
  start: params.start == null ? undefined : Number(params.start),
  vatFactor: params.vatFactor,
  ...(params.otherLinesChanged ? { otherLinesChanged: true } : {}),
});

export const contractProvision = (
  contract: IContractProvisionSource,
): IContractProvision => {
  const commonVatFactor = vatFactorOf({
    vatPct: contract.costsCommonVat,
    noVatType: contract.costsCommonNoVatType,
  });
  const nonCommonVatFactor = vatFactorOf({
    vatPct: contract.costsNonCommonVat,
    noVatType: contract.costsNonCommonNoVatType,
  });
  const lineVatFactor = (line: IContractCostLine) =>
    vatFactorOf({ vatPct: line.priceVat, noVatType: line.priceNoVatType });
  // Each line with its own VAT, rounded per line like the payment requests
  // Rentio sends for it: the costs are settled incl. VAT, and the billed
  // months already carry that VAT, so billed and not yet billed months share
  // one basis.
  const withVat = (amount: number | null | undefined, vatFactor: number) =>
    roundCents(Number(amount ?? 0) * vatFactor);
  const commonIsProvision = isProvision(contract.priceTypeCommonCosts);
  const nonCommonIsProvision = isProvision(contract.priceTypeNonCommonCosts);
  const additionalProvisions = (contract.additionalCosts ?? []).filter(line =>
    isProvision(line.priceType),
  );
  const additionalUnchanged = additionalProvisions.every(line =>
    sameAmount(line.startPrice, line.currentPrice),
  );
  const provisionLines: IProvisionLine[] = [
    ...(commonIsProvision
      ? [
          {
            key: COMMON_LINE_KEY,
            kind: "common" as const,
            slice: "common" as const,
            monthly: withVat(contract.currentCostsCommon, commonVatFactor),
          },
        ]
      : []),
    ...(nonCommonIsProvision
      ? [
          {
            key: NON_COMMON_LINE_KEY,
            kind: "nonCommon" as const,
            slice: "private" as const,
            monthly: withVat(
              contract.currentCostsNonCommon,
              nonCommonVatFactor,
            ),
          },
        ]
      : []),
    ...(contract.additionalCosts ?? []).flatMap((line, index) =>
      isProvision(line.priceType)
        ? [
            {
              key: `${ADDITIONAL_LINE_KEY_PREFIX}${index}`,
              kind: "additional" as const,
              slice: "private" as const,
              ...(line.description?.trim()
                ? { description: line.description.trim() }
                : {}),
              monthly: withVat(line.currentPrice, lineVatFactor(line)),
            },
          ]
        : [],
    ),
  ];
  const commonVatFactors = commonIsProvision ? [commonVatFactor] : [];
  const privateVatFactors = [
    ...(nonCommonIsProvision ? [nonCommonVatFactor] : []),
    ...additionalProvisions.map(lineVatFactor),
  ];

  // The provision is kept in two slices because a unit and its group are
  // separate ledgers: the common-costs provision regularizes against GROUP
  // costs, the non-common provision (plus the contract's additional
  // provision lines) against the unit's own costs.
  const monthlyProvisionCommon = commonIsProvision
    ? withVat(contract.currentCostsCommon, commonVatFactor)
    : 0;
  const monthlyProvisionPrivate = roundCents(
    (nonCommonIsProvision
      ? withVat(contract.currentCostsNonCommon, nonCommonVatFactor)
      : 0) +
      additionalProvisions.reduce(
        (sum, line) => sum + withVat(line.currentPrice, lineVatFactor(line)),
        0,
      ),
  );

  return {
    monthlyProvision: roundCents(
      monthlyProvisionCommon + monthlyProvisionPrivate,
    ),
    monthlyProvisionCommon,
    monthlyProvisionPrivate,
    // A provision that still equals its start amount was never indexed or
    // edited, so today's amount held for the lease's whole life: settling it
    // at that amount is certain, not an estimate (see provisionHistory.ts).
    provisionUnchanged: {
      common: sameAmount(
        contract.startCostsCommon,
        contract.currentCostsCommon,
      ),
      private:
        sameAmount(
          contract.startCostsNonCommon,
          contract.currentCostsNonCommon,
        ) && additionalUnchanged,
    },
    provisionIndexed: {
      ...(commonIsProvision
        ? {
            common: indexedField({
              current: contract.currentCostsCommon,
              start: contract.startCostsCommon,
              vatFactor: commonVatFactor,
            }),
          }
        : {}),
      ...(nonCommonIsProvision
        ? {
            private: indexedField({
              current: contract.currentCostsNonCommon,
              start: contract.startCostsNonCommon,
              vatFactor: nonCommonVatFactor,
              otherLinesChanged: !additionalUnchanged,
            }),
          }
        : {}),
    },
    provisionVatIncluded: {
      common: commonVatFactors.some(factor => factor > 1),
      private: privateVatFactors.some(factor => factor > 1),
    },
    provisionVatFactor: {
      ...(commonVatFactors.length > 0
        ? { common: sharedVatFactor(commonVatFactors) }
        : {}),
      ...(privateVatFactors.length > 0
        ? { private: sharedVatFactor(privateVatFactors) }
        : {}),
    },
    provisionLines,
  };
};

// The kinds a lease prices as a flat rate (forfait) with an amount, which a
// settlement never settles with its tenant (Lewis, 2026-10-16 and decision
// 20 of 2026-10-20; see forfaitKindsOf). Only the common and the non-common
// fields decide it: an extra cost line that is a forfait is simply ignored,
// and an extra line that is a provision belongs to the private kind and
// never makes a private forfait one to settle: no cost stands against it, so
// it goes back to the tenant in full (decision 3 of 2026-10-20, see the
// engine's withoutForfaitSlices). A field without an amount is no forfait.
export const leaseFixedFeeFlags = (
  contract: IContractProvisionSource,
): { fixedFeeCommon: boolean; fixedFeePrivate: boolean } => {
  const isFixedFee = (priceType?: string | null, amount?: number | null) =>
    priceType === EContractCostsPriceType.Fixed && Number(amount ?? 0) > 0;
  return {
    fixedFeeCommon: isFixedFee(
      contract.priceTypeCommonCosts,
      contract.currentCostsCommon,
    ),
    fixedFeePrivate: isFixedFee(
      contract.priceTypeNonCommonCosts,
      contract.currentCostsNonCommon,
    ),
  };
};
